import React, { useState } from 'react';
import {
  List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  Divider, Typography, Box,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import LockIcon from '@mui/icons-material/Lock';
import EmailIcon from '@mui/icons-material/Email';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import CloudIcon from '@mui/icons-material/Cloud';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTranslation } from 'react-i18next';
import { DirCategory, DriveFile, QuotaInfo } from '../services/myDriveService';
import QuotaBar from './QuotaBar';

interface Props {
  current: DirCategory;
  prefix: string;
  onChange: (cat: DirCategory) => void;
  onPrefixChange: (prefix: string) => void;
  files: DriveFile[];       // 該 category 完整 folder list（useCategoryFolders 抓）
  quota: QuotaInfo | null;
}

const CATEGORIES: Array<{ value: DirCategory; labelKey: string; icon: React.ReactNode }> = [
  { value: 'system',   labelKey: 'myDrive.catSystem',   icon: <LockIcon />         },
  { value: 'mail',     labelKey: 'myDrive.catMail',     icon: <EmailIcon />        },
  { value: 'photos',   labelKey: 'myDrive.catPhotos',   icon: <PhotoLibraryIcon /> },
  { value: 'personal', labelKey: 'myDrive.catPersonal', icon: <FolderIcon />       },
];

function _parentOf(path: string): string {
  return path.split('/').slice(0, -1).join('/');
}

interface TreeNodeProps {
  folder: DriveFile;
  allFolders: DriveFile[];
  currentPrefix: string;
  onPrefixChange: (p: string) => void;
  depth: number;
  expandedFolders: Set<string>;
  toggleExpand: (path: string) => void;
}

function TreeNode({
  folder, allFolders, currentPrefix, onPrefixChange,
  depth, expandedFolders, toggleExpand,
}: TreeNodeProps) {
  const folderPrefix = folder.relative_path + '/';
  const isCurrent = currentPrefix === folderPrefix;
  const isAncestor = currentPrefix.startsWith(folderPrefix) && !isCurrent;

  const children = allFolders.filter(f => _parentOf(f.relative_path) === folder.relative_path);
  const hasChildren = children.length > 0;

  // 展開：手動展開 OR 是 current / ancestor（自動展開）
  const isExpanded = expandedFolders.has(folder.relative_path) || isCurrent || isAncestor;

  return (
    <>
      <ListItemButton
        selected={isCurrent}
        sx={{ pl: 2 + depth * 1.5, py: 0.25 }}
      >
        {hasChildren ? (
          <Box
            sx={{ minWidth: 20, display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={(e) => { e.stopPropagation(); toggleExpand(folder.relative_path); }}
          >
            {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
          </Box>
        ) : (
          <Box sx={{ minWidth: 20 }} />
        )}
        <ListItemIcon sx={{ minWidth: 24 }}>
          <FolderIcon fontSize="small" color={isCurrent ? 'primary' : 'action'} />
        </ListItemIcon>
        <ListItemText
          primary={folder.filename}
          primaryTypographyProps={{ variant: 'caption', noWrap: true }}
          onClick={() => onPrefixChange(folderPrefix)}
          sx={{ cursor: 'pointer' }}
        />
      </ListItemButton>

      {isExpanded && children.map(child => (
        <TreeNode
          key={child.id} folder={child} allFolders={allFolders}
          currentPrefix={currentPrefix} onPrefixChange={onPrefixChange}
          depth={depth + 1}
          expandedFolders={expandedFolders} toggleExpand={toggleExpand}
        />
      ))}
    </>
  );
}

export default function SideNav({ current, prefix, onChange, onPrefixChange, files, quota }: Props) {
  const { t } = useTranslation();
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());

  const toggleExpand = (path: string) => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path); else next.add(path);
      return next;
    });
  };

  const rootFolders = files.filter(f => f.is_directory && _parentOf(f.relative_path) === '');

  return (
    <Box sx={{
      width: 240,
      borderRight: 1,
      borderColor: 'divider',
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        py: 1.5,
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
      }}>
        <CloudIcon fontSize="medium" />
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {t('myDrive.title', '我的網路硬碟')}
        </Typography>
      </Box>

      <Divider />
      <List dense disablePadding sx={{ flex: 1, overflow: 'auto' }}>
        {CATEGORIES.map(cat => (
          <Box key={cat.value}>
            {/* Category 頂層 */}
            <ListItem disablePadding>
              <ListItemButton
                selected={current === cat.value && prefix === ''}
                onClick={() => { onChange(cat.value); onPrefixChange(''); }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>{cat.icon}</ListItemIcon>
                <ListItemText
                  primary={t(cat.labelKey, cat.value)}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItemButton>
            </ListItem>
            {/* 只有 active category 展開子目錄樹（根層 folder 起遞迴）*/}
            {current === cat.value && rootFolders.map(folder => (
              <TreeNode
                key={folder.id} folder={folder} allFolders={files}
                currentPrefix={prefix} onPrefixChange={onPrefixChange}
                depth={0}
                expandedFolders={expandedFolders} toggleExpand={toggleExpand}
              />
            ))}
          </Box>
        ))}
      </List>
      <Divider />
      <Box sx={{ p: 1.5 }}>
        <QuotaBar quota={quota} />
      </Box>
    </Box>
  );
}
